/**
 * Referral Rewards Block - Frontend View
 *
 * Client-side rendering with animated expand/collapse for share options.
 */
import React, { useState } from 'react';
import { createRoot } from '@wordpress/element';
import { motion, AnimatePresence } from 'motion/react';
import { useEnrollment } from '@blocks/_shared/hooks/use-enrollment';
import Button from '@blocks/_shared/button';
import {
	Item,
	ItemContent,
	ItemLabel,
	ItemDescription,
} from '@blocks/_shared/components';
import { SocialShareGrid, type SocialPlatform } from '~/shared/components/social-share';
import { ReferralProvider } from './context';
import './style.scss';

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	friendHeading: string;
	friendDescription: string;
	yourHeading: string;
	yourDescription: string;
	buttonText: string;
	buttonTextJoin: string;
	buttonTextSignIn: string;
	expandedHeading: string;
	shareMessage: string;
	cardLabelLevel: number;
	showReferralStats: boolean;
	referredLabel: string;
	completedLabel: string;
	statCardBackgroundColor?: string;
	// Native Gutenberg color attributes (applied as inline styles by WordPress)
	// textColor, backgroundColor, style.color.* are handled automatically
}

// ============================================================================
// Icon Components (Lucide-style SVGs)
// ============================================================================

const UsersIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
		<circle cx="9" cy="7" r="4" />
		<path d="M22 21v-2a4 4 0 0 0-3-3.87" />
		<path d="M16 3.13a4 4 0 0 1 0 7.75" />
	</svg>
);

const UserCheckIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
		<circle cx="9" cy="7" r="4" />
		<polyline points="16 11 18 13 22 9" />
	</svg>
);

interface ReferralData {
	referralCode: string;
	referralUrl: string;
	referredContacts: number;
	completedReferrals: number;
}

// ============================================================================
// Main Component
// ============================================================================

const ReferralRewardsView = ({ attributes }: { attributes: Attributes }) => {
	const { status } = useEnrollment();
	const [isExpanded, setIsExpanded] = useState(false);
	const [referralData, setReferralData] = useState<ReferralData | null>(null);
	const [copiedToClipboard, setCopiedToClipboard] = useState(false);

	// Load referral data from window (injected by PHP)
	React.useEffect(() => {
		if (window.leatReferralConfig) {
			setReferralData({
				referralCode: window.leatReferralConfig.referralCode || '',
				referralUrl: window.leatReferralConfig.homeUrl
					? `${window.leatReferralConfig.homeUrl}?ref=${window.leatReferralConfig.referralCode}`
					: '',
				referredContacts: window.leatReferralConfig.referredContacts || 0,
				completedReferrals: window.leatReferralConfig.completedReferrals || 0,
			});
		}
	}, []);

	// Note: Button colors are applied automatically via wp-block-button__link class
	// No manual color extraction needed - Gutenberg handles it!

	const LabelTag = `h${attributes.cardLabelLevel}` as keyof JSX.IntrinsicElements;

	// Handle copy to clipboard
	const handleCopyLink = async () => {
		if (referralData?.referralUrl) {
			try {
				await navigator.clipboard.writeText(referralData.referralUrl);
				setCopiedToClipboard(true);
				setTimeout(() => setCopiedToClipboard(false), 2000);
			} catch (err) {
				console.error('Failed to copy:', err);
			}
		}
	};

	// Handle social share tracking
	const handleSocialShare = (platform: SocialPlatform) => {
		console.log(`Shared on ${platform}`);
		// You can add analytics tracking here
	};

	// Render based on enrollment status
	switch (status) {
		case 'guest':
			return (
				<div className="leat-referral-rewards">
					<div className="leat-referral-rewards__grid">
						<Item variant="default">
							<ItemContent>
								<ItemLabel as={LabelTag}>{attributes.friendHeading}</ItemLabel>
								<ItemDescription>{attributes.friendDescription}</ItemDescription>
							</ItemContent>
						</Item>
						<Item variant="default">
							<ItemContent>
								<ItemLabel as={LabelTag}>{attributes.yourHeading}</ItemLabel>
								<ItemDescription>{attributes.yourDescription}</ItemDescription>
							</ItemContent>
						</Item>
					</div>
					<div className="leat-referral-rewards__button-wrapper">
						<Button
							href={`/wp-login.php?redirect_to=${encodeURIComponent(window.location.href)}`}
							variant="contained"
							className="wp-block-button__link"
						>
							{attributes.buttonTextSignIn}
						</Button>
					</div>
				</div>
			);

		case 'loggedInNotEnrolled':
			return (
				<div className="leat-referral-rewards">
					<div className="leat-referral-rewards__grid">
						<Item variant="default">
							<ItemContent>
								<ItemLabel as={LabelTag}>{attributes.friendHeading}</ItemLabel>
								<ItemDescription>{attributes.friendDescription}</ItemDescription>
							</ItemContent>
						</Item>
						<Item variant="default">
							<ItemContent>
								<ItemLabel as={LabelTag}>{attributes.yourHeading}</ItemLabel>
								<ItemDescription>{attributes.yourDescription}</ItemDescription>
							</ItemContent>
						</Item>
					</div>
					<div className="leat-referral-rewards__button-wrapper">
						<Button href="/my-account/" variant="contained" className="wp-block-button__link">
							{attributes.buttonTextJoin}
						</Button>
					</div>
				</div>
			);

		case 'enrolled':
			return (
				<div className="leat-referral-rewards">
					<div className="leat-referral-rewards__grid">
						<Item variant="default">
							<ItemContent>
								<ItemLabel as={LabelTag}>{attributes.friendHeading}</ItemLabel>
								<ItemDescription>{attributes.friendDescription}</ItemDescription>
							</ItemContent>
						</Item>
						<Item variant="default">
							<ItemContent>
								<ItemLabel as={LabelTag}>{attributes.yourHeading}</ItemLabel>
								<ItemDescription>{attributes.yourDescription}</ItemDescription>
							</ItemContent>
						</Item>
					</div>

					<div className="leat-referral-rewards__button-wrapper">
						<Button
							onClick={() => setIsExpanded(!isExpanded)}
							variant="contained"
							className="wp-block-button__link"
						>
							{attributes.buttonText}
						</Button>
					</div>

					{/* Animated Expanded Section */}
					<AnimatePresence initial={false}>
						{isExpanded && referralData && (
							<motion.div
								className="leat-referral-rewards__expanded"
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: 'auto' }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 0.3, ease: 'easeInOut' }}
							>
								<motion.div
									initial={{ y: -20 }}
									animate={{ y: 0 }}
									transition={{ duration: 0.3, delay: 0.1 }}
								>
									<p className="leat-referral-rewards__expanded-heading">
										{attributes.expandedHeading}
									</p>

									{/* Referral Link Display */}
									<div className="leat-referral-rewards__link-display">
										<code className="leat-referral-rewards__link">{referralData.referralUrl}</code>
										<button
											type="button"
											className="leat-referral-rewards__copy-button"
											onClick={handleCopyLink}
										>
											{copiedToClipboard ? 'Copied!' : 'Copy'}
										</button>
									</div>

									{/* Social Share Grid */}
									<div className="leat-referral-rewards__social-wrapper">
										<SocialShareGrid
											url={referralData.referralUrl}
											message={attributes.shareMessage}
											onShare={handleSocialShare}
											className="leat-referral-rewards__social-grid"
										/>
									</div>
								</motion.div>
							</motion.div>
						)}
					</AnimatePresence>

					{/* Referral Stats Section */}
					{attributes.showReferralStats && referralData && (
						<div className="leat-referral-rewards__stats">
							<div
								className="leat-referral-rewards__stat-card"
								style={attributes.statCardBackgroundColor ? { backgroundColor: attributes.statCardBackgroundColor } : undefined}
							>
								<UsersIcon />
								<span className="leat-referral-rewards__stat-label">{attributes.referredLabel}</span>
								<span className="leat-referral-rewards__stat-value">{referralData.referredContacts}</span>
							</div>
							<div
								className="leat-referral-rewards__stat-card"
								style={attributes.statCardBackgroundColor ? { backgroundColor: attributes.statCardBackgroundColor } : undefined}
							>
								<UserCheckIcon />
								<span className="leat-referral-rewards__stat-label">{attributes.completedLabel}</span>
								<span className="leat-referral-rewards__stat-value">{referralData.completedReferrals}</span>
							</div>
						</div>
					)}
				</div>
			);

		default:
			return null;
	}
};

// ============================================================================
// Initialization
// ============================================================================

export function initReferralRewardsBlocks() {
	const blocks = document.querySelectorAll<HTMLElement>('.leat-referral-rewards');

	for (const block of blocks) {
		// Parse the style attribute if it exists
		let style: Attributes['style'];
		if (block.dataset.style) {
			try {
				style = JSON.parse(block.dataset.style);
			} catch (e) {
				console.error('Failed to parse style attribute:', e);
			}
		}

		const attributes: Attributes = {
			friendHeading: block.dataset.friendHeading || 'Your friend gets',
			friendDescription: block.dataset.friendDescription || 'Sign up to receive 1,000 points',
			yourHeading: block.dataset.yourHeading || 'You get',
			yourDescription: block.dataset.yourDescription || 'Get 1,000 points when your friends sign up',
			buttonText: block.dataset.buttonText || 'Share Referral Link',
			buttonTextJoin: block.dataset.buttonTextJoin || 'Join Program',
			buttonTextSignIn: block.dataset.buttonTextSignIn || 'Sign In',
			expandedHeading: block.dataset.expandedHeading || 'Share your referral link',
			shareMessage: block.dataset.shareMessage || 'Check out this loyalty program!',
			cardLabelLevel: Number(block.dataset.cardLabelLevel) || 4,
			showReferralStats: block.dataset.showReferralStats !== 'false',
			referredLabel: block.dataset.referredLabel || 'Referred',
			completedLabel: block.dataset.completedLabel || 'Completed',
			statCardBackgroundColor: block.dataset.statCardBackgroundColor || undefined,
			textColor: block.dataset.textColor || undefined,
			backgroundColor: block.dataset.backgroundColor || undefined,
			style: style || undefined,
		};

		createRoot(block).render(<ReferralRewardsView attributes={attributes} />);
	}
}

// Auto-initialize
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initReferralRewardsBlocks);
} else {
	initReferralRewardsBlocks();
}

// Extend window interface for referral config
declare global {
	interface Window {
		leatReferralConfig?: {
			referralCode: string;
			referredContacts: number;
			completedReferrals: number;
			homeUrl: string;
			isLoggedIn: boolean;
			ajaxUrl: string;
			nonce: string;
		};
	}
}
